import React from 'react';
import { motion } from 'framer-motion';
import CountUp from 'react-countup';

// Simplified icons, replace with actual SVGs or library
const IconPlaceholder = ({ emoji }) => (
    <div className="flex-shrink-0 w-12 h-12 bg-blue-100 text-primary rounded-xl flex items-center justify-center text-2xl">
        {emoji}
    </div>
);

const aboutPoints = [
    { emoji: '👨‍💻', title: 'Expert Consultants', description: 'Our team consists of certified professionals with years of experience in enterprise solutions.' },
    { emoji: '🔧', title: 'Tailored Solutions', description: 'We customize every implementation to match your specific business requirements.' },
    { emoji: '🛡️', title: 'Ongoing Support', description: 'We provide continuous support and maintenance after implementation.' },
    { emoji: '🎓', title: 'Training & Knowledge Transfer', description: 'Comprehensive training programs to empower your team with new technology.' }
];

const aboutStats = [
    { value: '100+', label: 'Projects Completed' },
    { value: '50+', label: 'Happy Clients' },
    { value: '25+', label: 'Expert Consultants' }
];

function About() {

    const sectionSlideInLeft = {
        hidden: { opacity: 0, x: -100 },
        visible: { 
            opacity: 1, 
            x: 0, 
            transition: { duration: 0.6, ease: "easeOut", staggerChildren: 0.2, delayChildren: 0.1 }
        }
    };

    const itemVariants = {
        hidden: { opacity: 0, y: 20 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
    };

    const imageVariants = {
        hidden: { opacity: 0, x: -30 },
        visible: { opacity: 1, x: 0, transition: { duration: 0.6, ease: "easeOut" } }
    };

    const statsContainerVariants = {
        hidden: {}, 
        visible: { transition: { staggerChildren: 0.1 } }
    };

    const statItemVariants = {
        hidden: { opacity: 0, y: 10 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.4 } }
    };

  return (
    <section id="about" className="py-24 bg-gradient-to-tr from-primary/5 to-white overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
            className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            variants={sectionSlideInLeft}
            >
          {/* Left Column: Image */}
          <motion.div variants={imageVariants}>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80"
                alt="ForceSAP Team"
                className="rounded-xl shadow-xl w-full h-auto"
              />
              {/* Decorative Elements - could also be animated */}
              <div className="absolute -bottom-8 -left-8 h-24 w-24 bg-primary/10 rounded-full z-0"></div>
              <div className="absolute -top-8 -right-8 h-32 w-32 bg-secondary/10 rounded-full z-0"></div>
              <motion.div
                 className="absolute -bottom-6 -right-6 bg-white p-4 rounded-lg shadow-lg"
                 initial={{ opacity: 0, scale: 0.8 }}
                 animate={{ opacity: 1, scale: 1 }}
                 transition={{ delay: 0.5, duration: 0.4 }}
                 >
                <p className="text-xl font-bold text-primary">15+ Years</p>
                <p className="text-sm text-gray-600">Industry Experience</p>
              </motion.div>
            </div>
          </motion.div>

          {/* Right Column: Text Content */}
          <motion.div variants={itemVariants} >
            <div>
              <span className="text-primary font-semibold tracking-wider uppercase">About Us</span>
              <h2 className="text-4xl font-bold text-gray-900 mt-2 mb-6">Leading Enterprise Solutions Provider</h2>
              <p className="text-lg text-gray-600 mb-6">
                Founded with a vision to transform businesses through technology, ForceSAP is a premium provider of enterprise solutions specializing in Salesforce, SAP, and Microsoft Dynamics 365.
              </p>
              <p className="text-lg text-gray-600 mb-8">
                Our team of certified experts brings years of industry experience to deliver tailored solutions that drive efficiency, growth, and innovation for our clients across various sectors.
              </p>
            </div>

            {/* Points Grid */}
            <motion.div
                className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8"
                variants={statsContainerVariants}
            >
              {aboutPoints.map((point, index) => (
                <motion.div key={index} className="flex gap-4" variants={statItemVariants}>
                    <IconPlaceholder emoji={point.emoji} />
                    <div>
                        <h3 className="text-lg font-semibold text-gray-800 mb-1">{point.title}</h3>
                        <p className="text-gray-600 text-sm">{point.description}</p>
                    </div>
                </motion.div>
              ))}
            </motion.div>

            {/* Stats Row - Updated with CountUp */}
            <motion.div
                className="flex flex-wrap gap-8 mt-12 items-center"
                variants={statsContainerVariants}
            >
                {aboutStats.map((stat, index) => {
                    const endValue = parseInt(stat.value.replace(/\D/g, ''), 10);
                    const suffix = stat.value.replace(/\d/g, '') || '';
                    
                    return (
                        <React.Fragment key={index}>
                            <motion.div variants={statItemVariants}>
                                <p className="text-3xl font-bold text-primary mb-1">
                                    <CountUp 
                                        end={endValue} 
                                        duration={2.5} 
                                        enableScrollSpy
                                        scrollSpyOnce
                                    />
                                    {suffix}
                                </p>
                                <p className="text-sm text-gray-600">{stat.label}</p>
                            </motion.div>
                            {index < aboutStats.length - 1 && 
                                <motion.div className="h-12 w-px bg-gray-200" variants={statItemVariants}></motion.div>}
                        </React.Fragment>
                    );
                })}
            </motion.div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}

export default About; 